import {
  BankOutlined,
  CalculatorOutlined,
  CalendarOutlined,
  DollarOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  PushpinOutlined,
  ScheduleOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Card, Progress, Tooltip as AntTooltip } from 'antd';
import { getCustomerOrdersById } from 'api/customers';
import { PERMISSIONS } from 'constants/permissions';
import { ModalContext } from 'context/modalContext';
import { OrderStatusLabel } from 'constants/status';
import dayjs from 'dayjs';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  errorHandling,
  getData,
  priceTransformer,
} from 'utils';
import { COMPLETED, DELIVERED } from 'constants/status';
import { captureException } from 'utils/errors';
import { WL_CONFIG } from 'constants';
import Tooltip from 'components/Tooltip';
import Addresses from '../Addresses';
import BannedSwitch from './BanndedSwitch';
import AssignCompanyModal from './AssignCompanyModal';
import DietaryPreferencesModal from './DietaryPreferencesModal';
import EditAccountModal from './EditAccountModal';
import VipSwitch from './VipSwitch';
import { DEMO_PLACEHOLDERS } from './constants';
import { StyledWrapper } from './styles';
import TaxFreeSwitch from './TaxFreeSwitch';

function Overview({ customer, setCustomer }) {
  const { handleModal } = useContext(ModalContext);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [editAccountOpen, setEditAccountOpen] = useState(false);
  const [assignCompanyOpen, setAssignCompanyOpen] = useState(false);
  const [dietaryPreferencesOpen, setDietaryPreferencesOpen] = useState(false);
  const navigate = useNavigate();
  const hasCustomerAccess = useHasAnyOfPermissions([PERMISSIONS.CUSTOMERS]);

  useEffect(() => {
    if (!hasCustomerAccess || !customer?.id) return;
    setOrdersLoading(true);
    getCustomerOrdersById(customer.id, 1, { page_size: 100 })
      .then((res) => {
        const data = getData(res);
        const results = data?.data?.results ?? data?.results ?? [];
        setOrders(Array.isArray(results) ? results : []);
        setOrdersLoading(false);
      })
      .catch((e) => {
        captureException(e);
        setOrdersLoading(false);
        errorHandling(e);
      });
  }, [hasCustomerAccess, customer?.id]);

  const handleFormatDate = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '-');
  const handleFormatSignUpDate = (d) => (d ? dayjs(d).format('MMM D, YYYY') : '-');

  // Real: computed from last_order_date
  const daysSinceLastOrder = customer?.last_order_date
    ? dayjs().diff(dayjs(customer.last_order_date), 'day')
    : null;

  // Real: total_spent / orders_count
  const avgOrderValue = customer?.orders_count > 0 && customer?.total_spent != null
    ? customer.total_spent / customer.orders_count
    : null;

  // Format currency with commas (e.g. $1,234.56)
  const formatCurrency = (val) => (val != null
    ? `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : null);

  // Real: average days between orders = (last - first) / (orders_count - 1) when orders_count > 1, floor of 1
  const avgDaysBetweenOrdersRaw = (customer?.orders_count > 1 && customer?.first_order_date && customer?.last_order_date)
    ? dayjs(customer.last_order_date).diff(dayjs(customer.first_order_date), 'day') / (customer.orders_count - 1)
    : null;
  const avgDaysBetweenOrders = avgDaysBetweenOrdersRaw != null
    ? (() => {
        const floored = Math.max(1, Math.round(avgDaysBetweenOrdersRaw * 10) / 10);
        return Number.isInteger(floored) ? String(floored) : floored.toFixed(1);
      })()
    : null;

  // Repeat probability: RFM-inspired (recency + frequency) / 10 * 100
  const getRepeatProbability = () => {
    if (!customer?.orders_count || customer.orders_count === 0) return null;
    const days = daysSinceLastOrder ?? 999;
    const recency = days <= 7 ? 5 : days <= 14 ? 4 : days <= 30 ? 3 : days <= 60 ? 2 : 1;
    const freq = customer.orders_count <= 2 ? 1 : customer.orders_count <= 5 ? 2 : customer.orders_count <= 10 ? 3 : customer.orders_count <= 20 ? 4 : 5;
    return Math.min(100, Math.round(((recency + freq) / 10) * 100));
  };
  const repeatProbability = getRepeatProbability();

  // Real: orders_count / months since first order (or created_at)
  const firstOrderDateForFreq = customer?.first_order_date ?? customer?.created_at;
  const monthsActive = firstOrderDateForFreq
    ? Math.max(1, dayjs().diff(dayjs(firstOrderDateForFreq), 'month'))
    : 1;
  const frequencyPerMonthRaw = customer?.orders_count > 0
    ? customer.orders_count / monthsActive
    : null;
  const frequencyPerMonth = frequencyPerMonthRaw != null
    ? (Number.isInteger(frequencyPerMonthRaw) ? String(frequencyPerMonthRaw) : frequencyPerMonthRaw.toFixed(1))
    : null;

  const last5Orders = orders.slice(0, 5);

  // Revenue trend: last 6 months from completed/delivered orders
  const completedStates = [COMPLETED, DELIVERED];
  const revenueOrders = orders.filter((o) => completedStates.includes(Number(o.state)));
  const monthLabels = [];
  for (let i = 5; i >= 0; i--) {
    monthLabels.push(dayjs().subtract(i, 'month').format('MMM'));
  }
  const revenueByMonth = monthLabels.map((label, idx) => {
    const monthStart = dayjs().subtract(5 - idx, 'month').startOf('month');
    const monthEnd = monthStart.endOf('month');
    const total = revenueOrders
      .filter((o) => {
        const d = o.state_modified || o.created_at;
        if (!d) return false;
        const ts = dayjs(d).valueOf();
        return ts >= monthStart.valueOf() && ts <= monthEnd.valueOf();
      })
      .reduce((sum, o) => sum + (Number(o.value) || 0), 0);
    return { month: label, revenue: total };
  });
  const last6MonthsTotal = revenueByMonth.reduce((s, r) => s + r.revenue, 0);
  const activeMonthsCount = revenueByMonth.filter((r) => r.revenue > 0).length;
  const avgMonthly = activeMonthsCount > 0 ? last6MonthsTotal / activeMonthsCount : 0;
  const maxRevenue = Math.max(...revenueByMonth.map((r) => r.revenue), 1);
  const BAR_MAX_HEIGHT_PX = 120;

  const getOrderStatusLabel = (state) => OrderStatusLabel[state] ?? 'Unknown';

  const getOrderStatusClass = (state) => {
    const statusMap = {
      0: 'status--new',
      1: 'status--in-progress',
      2: 'status--cancelled',
      4: 'status--ready',
      5: 'status--on-the-way',
      6: 'status--completed',
      9: 'status--stranded',
      11: 'status--needs-approval',
      12: 'status--delivered',
    };
    return statusMap[state] ?? 'status--completed';
  };

  const handleGotToEditPage = (address) => {
    navigate(`/console/customer-address/${customer.id}/${address?.id}`);
  };

  const handleOpenAddressesModal = (addresses) => {
    handleModal(
      <Addresses
        addresses={addresses}
        goToEditPage={(address) => handleGotToEditPage(address)}
      />,
      false,
      'modal-close-btn',
    );
  };

  const primaryAddress = customer?.customer_addresses?.length > 0
    ? customer.customer_addresses[customer.customer_addresses.length - 1]
    : null;
  const addressDisplay = primaryAddress
    ? [primaryAddress.address, primaryAddress.city, primaryAddress.state, primaryAddress.zip_code]
      .filter(Boolean)
      .join(', ')
    : '-';

  if (!customer) return null;

  return (
    <StyledWrapper>
      {/* Profile Header */}
      <div className="profile-header">
        <Avatar
          size={64}
          icon={<UserOutlined />}
          className="profile-avatar"
          style={{ backgroundColor: WL_CONFIG.colors.primary }}
        >
          {customer.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div className="profile-info">
          <h1 className="customer-name">
            {customer.name || 'No name'}
            {customer.is_vip && <span className="vip-badge">VIP</span>}
          </h1>
          <div className="profile-actions">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setEditAccountOpen(true)}
            >
              Edit Account
            </Button>
            <AntTooltip title={customer?.dietary_notes || null}>
              <Button
                icon={<UserOutlined />}
                onClick={() => setDietaryPreferencesOpen(true)}
              >
                Dietary Preferences
              </Button>
            </AntTooltip>
          </div>
          <div className="profile-details-grid">
            <div className="profile-detail">
              <span className="label">Company:</span>
              <span className="value">
                {customer.company_detail ? (
                  <>
                    <a
                      href={`#/console/customers/companies/${customer.company_detail.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/console/customers/companies/${customer.company_detail.id}`);
                      }}
                    >
                      {customer.company_detail.name}
                    </a>
                    {' · '}
                    <Button type="link" size="small" onClick={() => setAssignCompanyOpen(true)}>
                      Change
                    </Button>
                  </>
                ) : (
                  <Button type="link" size="small" onClick={() => setAssignCompanyOpen(true)}>
                    Assign to company
                  </Button>
                )}
              </span>
            </div>
            {customer.company_detail?.industry && (
              <div className="profile-detail">
                <span className="label">Industry:</span>
                <span className="value">{customer.company_detail.industry}</span>
              </div>
            )}
            {/* Real: computed from last_order_date */}
            <div className="profile-detail">
              <span className="label">Days Since Last Order:</span>
              <span className="value">
                {daysSinceLastOrder != null ? `${daysSinceLastOrder} Days` : '-'}
              </span>
            </div>
          </div>
          {/* PLACEHOLDER: No customer scoring logic exists */}
          <div className="customer-score">
            <div className="customer-score-header">
              <span className="label">Customer Score</span>
              <Tooltip
                tooltipBoxClasses="tooltip-box"
                label={<InfoCircleOutlined className="customer-score-info-icon" />}
                tooltipBody={(
                  <span>
                    Customer score is a calculated score based on the customer&apos;s history and behavior - many factors go into this score.
                  </span>
                )}
                tooltipBodyClasses="tooltip"
              />
            </div>
            <Progress
              percent={DEMO_PLACEHOLDERS.customerScore}
              strokeColor="#4BB43A"
              showInfo
              size="small"
              className="score-bar"
            />
          </div>
        </div>
      </div>

      {/* Contact Bar - purple with white text */}
      <div className="contact-bar">
        <span><MailOutlined /> {customer.email || '-'}</span>
        <span><CalendarOutlined /> Customer Since: {handleFormatSignUpDate(customer.created_at)}</span>
        <span><PhoneOutlined /> {customer.phone || '-'}</span>
        <span><PushpinOutlined /> {addressDisplay}</span>
      </div>

      {/* Revenue Snapshot & Trend */}
      <div className="dashboard-grid">
        <Card className="revenue-card" title="Revenue Snapshot">
          <div className="revenue-metrics">
            <div className="metric">
              <ShoppingCartOutlined className="metric-icon" />
              <div>
                <div className="metric-label">Order Count</div>
                <div className="metric-value">{customer?.orders_count ?? 0}</div>
              </div>
            </div>
            <div className="metric">
              <BankOutlined className="metric-icon" />
              <div>
                <div className="metric-label">Customer Lifetime Value</div>
                <div className="metric-value">{formatCurrency(customer?.total_spent ?? 0)}</div>
              </div>
            </div>
            <div className="metric">
              <CalculatorOutlined className="metric-icon" />
              <div>
                <div className="metric-label">Average Order Value</div>
                <div className="metric-value">{formatCurrency(avgOrderValue ?? 0)}</div>
              </div>
            </div>
            <div className="metric">
              <ScheduleOutlined className="metric-icon" />
              <div>
                <div className="metric-label">Frequency</div>
                <div className="metric-value">
                  {customer?.orders_count === 0 ? 'No Orders' : `${frequencyPerMonth ?? 0} orders per month`}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="revenue-trend-card" title="Revenue Trend">
          <div className="revenue-trend-chart">
            {revenueByMonth.map(({ month, revenue }) => (
              <div key={month} className="trend-bar-wrapper">
                <AntTooltip title={`${month}: ${priceTransformer(revenue)}`} placement="top">
                  <div
                    className="trend-bar"
                    style={{
                      height: `${Math.max(4, (revenue / maxRevenue) * BAR_MAX_HEIGHT_PX)}px`,
                    }}
                  />
                </AntTooltip>
                <span className="trend-label">{month}</span>
              </div>
            ))}
          </div>
          <div className="trend-summary">
            <div>
              <span className="label">Last 6 Months:</span>
              <strong> {priceTransformer(last6MonthsTotal)}</strong>
            </div>
            <div>
              <span className="label">Avg Monthly:</span>
              <strong> {priceTransformer(avgMonthly)}</strong>
            </div>
          </div>
        </Card>
      </div>

      {/* Meal Period & Metrics */}
      <div className="dashboard-grid">
        <Card className="meal-breakdown-card" title="Meal Period Breakdown">
          {/* PLACEHOLDER: meal_type from OrderListSerializer when available */}
          <div className="donut-container">
            <div
              className="meal-donut"
              style={{
                background: `conic-gradient(
                  #E5DFFC 0% ${DEMO_PLACEHOLDERS.mealPeriodBreakdown[0].value}%,
                  #9B7DD4 ${DEMO_PLACEHOLDERS.mealPeriodBreakdown[0].value}% ${DEMO_PLACEHOLDERS.mealPeriodBreakdown[0].value + DEMO_PLACEHOLDERS.mealPeriodBreakdown[1].value}%,
                  #672792 ${DEMO_PLACEHOLDERS.mealPeriodBreakdown[0].value + DEMO_PLACEHOLDERS.mealPeriodBreakdown[1].value}% 100%
                )`,
              }}
            />
          </div>
          <div className="meal-legend">
            {DEMO_PLACEHOLDERS.mealPeriodBreakdown.map(({ period, value }) => (
              <div key={period} className="legend-item">
                <span className="dot" />
                <span>{period}: {value}%</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="metrics-column">
          <Card className="metric-card">
            <div className="metric-card-content">
              <span className="metric-card-label">Average Days Between Orders</span>
              <Tooltip
                tooltipBoxClasses="tooltip-box"
                label={<InfoCircleOutlined className="info-icon" />}
                tooltipBody={(
                  <span>
                    Average days between consecutive orders. Displayed values have a minimum of 1 day.
                  </span>
                )}
                tooltipBodyClasses="tooltip"
              />
            </div>
            <div className="metric-card-value">
              {avgDaysBetweenOrders != null ? `${avgDaysBetweenOrders} days` : '-'}
            </div>
          </Card>
          <Card className="metric-card">
            <div className="metric-card-content">
              <span className="metric-card-label">Repeat Probability</span>
              <Tooltip
                tooltipBoxClasses="tooltip-box"
                label={<InfoCircleOutlined className="info-icon" />}
                tooltipBody={(
                  <span>
                    Based on recency (days since last order) and frequency (order count). Higher scores indicate customers more likely to order again.
                  </span>
                )}
                tooltipBodyClasses="tooltip"
              />
            </div>
            <div className="metric-card-value">
              {repeatProbability != null ? `${repeatProbability}%` : '-'}
            </div>
          </Card>

          <Card className="last-orders-card" title="Last 5 Orders">
            <table className="last-orders-table">
              <thead>
                <tr className="table-header">
                  <th className="col-date">Order date</th>
                  <th className="col-amount">Amount</th>
                  <th className="col-status">Status</th>
                </tr>
              </thead>
              <tbody>
                {ordersLoading ? (
                  <tr><td colSpan={3} className="loading-row">Loading...</td></tr>
                ) : last5Orders.length === 0 ? (
                  <tr><td colSpan={3} className="empty-row">No orders yet</td></tr>
                ) : (
                  last5Orders.map((order, index) => {
                    const orderId = order.id ?? order.pk;
                    const base = (process.env.REACT_APP_BASENAME || '').replace(/\/$/, '');
                    const orderUrl = orderId ? `${window.location.origin}${base}/console/orders/${orderId}` : '#';
                    const orderDate = order.state_modified || order.created_at;
                    return (
                    <tr key={orderId || order.id || index}>
                      <td className="col-date">
                        <a
                          href={hasCustomerAccess ? orderUrl : '#'}
                          target={hasCustomerAccess ? '_blank' : undefined}
                          rel={hasCustomerAccess ? 'noopener noreferrer' : undefined}
                          className="table-row-link"
                        >
                          {orderDate ? dayjs(orderDate).format('MMM D, YYYY') : '-'}
                        </a>
                      </td>
                      <td className="col-amount strong">
                        <a
                          href={hasCustomerAccess ? orderUrl : '#'}
                          target={hasCustomerAccess ? '_blank' : undefined}
                          rel={hasCustomerAccess ? 'noopener noreferrer' : undefined}
                          className="table-row-link"
                        >
                          {priceTransformer(order.value ?? 0)}
                        </a>
                      </td>
                      <td className="col-status">
                        <a
                          href={hasCustomerAccess ? orderUrl : '#'}
                          target={hasCustomerAccess ? '_blank' : undefined}
                          rel={hasCustomerAccess ? 'noopener noreferrer' : undefined}
                          className={`status-pill ${getOrderStatusClass(order.state)}`}
                        >
                          {getOrderStatusLabel(order.state)}
                        </a>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Card>
        </div>
      </div>

      {/* Account Toggles - tile based */}
      <div className="account-toggles-tiles">
        <div className="toggle-tile">
          <VipSwitch customer={customer} onChange={(is_vip) => setCustomer((c) => ({ ...c, is_vip }))} />
        </div>
        <div className="toggle-tile">
          <BannedSwitch customer={customer} onChange={(is_banned) => setCustomer((c) => ({ ...c, is_banned }))} />
        </div>
        <div className="toggle-tile">
          <TaxFreeSwitch customer={customer} onChange={(is_tax_exempt) => setCustomer((c) => ({ ...c, is_tax_exempt }))} />
        </div>
        <div className="toggle-tile wallet-tile">
          <div className="wallet-tile-content">
            <div className="wallet-tile-header">
              <WalletOutlined className="wallet-icon" />
              <span className="wallet-label">Wallet Balance</span>
            </div>
            <div className="wallet-balance">
              {priceTransformer((customer?.wallet?.balance ?? 0) / 100)}
            </div>
            <Button
              type="link"
              size="small"
              className="adjust-balance-link"
              onClick={() => navigate(`/console/customers/${customer.id}/adjust-balance`)}
            >
              Adjust balance
            </Button>
          </div>
        </div>
      </div>

      <EditAccountModal
        open={editAccountOpen}
        customer={customer}
        onClose={() => setEditAccountOpen(false)}
        onSuccess={(data) => setCustomer((c) => ({ ...c, ...data }))}
      />
      <AssignCompanyModal
        open={assignCompanyOpen}
        customer={customer}
        onClose={() => setAssignCompanyOpen(false)}
        onSuccess={(data) => setCustomer((c) => ({
          ...c,
          company: data?.company ?? (data?.company_detail?.id ?? null),
          company_detail: data?.company_detail ?? null,
        }))}
      />
      <DietaryPreferencesModal
        open={dietaryPreferencesOpen}
        customer={customer}
        onClose={() => setDietaryPreferencesOpen(false)}
        onSuccess={(data) => setCustomer((c) => ({ ...c, ...data }))}
      />
    </StyledWrapper>
  );
}

export default Overview;
